import { ContentAuditReport } from '../types/content_audit'
import { StatusTypes } from '../types/editor'

export function isAuditCompleted(data: ContentAuditReport) {
  return Object.values(data).every(value => {
    if (typeof value === 'object' && 'status' in value) {
      return value.status !== StatusTypes.IN_PROGRESS
    }
    return true
  })
}

export const getPlagiarismDetails = (
  plagiarismCount: number
): AuditSetResponse => {
  if (plagiarismCount <= 10) {
    const text = (
      <span>
        Looks good! Honesty and original content are the best policies.
        <span role="img" aria-label="smile-emoji">
          😃
        </span>
        Automated tools are not great at detecting advanced forms of plagiarism
        though.
        <a
          target="_blank"
          rel="noreferrer"
          href="https://peppercontent.freshdesk.com/support/solutions/articles/67000675333-content-audit-plagiarism-score"
        >
          Read our guidelines here.
        </a>
      </span>
    )
    return {
      color: 'green',
      sign: 'tick',
      text
    }
  } else if (plagiarismCount <= 25) {
    return {
      color: 'yellow',
      sign: 'exclamation',
      text: `Some phrases in your content seem plagiarized or overly generic. Sometimes this is inevitable, but we recommend keeping it below 5%.`
    }
  } else if (plagiarismCount <= 40) {
    return {
      color: 'orange',
      sign: 'exclamation',
      text: `Quite a bit of this content seems to match other sources on the web. Generally, this should be less than 5%.`
    }
  }
  return {
    color: 'red',
    sign: 'exclamation',
    text: `Something is wrong here. Can you check if you've not copied content from another source? Generally, this should be less than 5%.`
  }
}
export const getGrammarDetails = (grammarCount: number): AuditSetResponse => {
  if (grammarCount <= 1) {
    return {
      color: 'green',
      sign: 'tick',
      text: `Nice work! We didn't find significant errors in your content.`
    }
  } else if (grammarCount <= 3) {
    return {
      color: 'yellow',
      sign: 'exclamation',
      text: `There are a few grammatical oversights (spelling, sentence structure) in your content. An error rate below 1% is generally acceptable.`
    }
  } else if (grammarCount <= 10) {
    return {
      color: 'orange',
      sign: 'exclamation',
      text: `Do you need to go back and make some changes? Great content relies on correctness and clarity, and an error rate below 1% is generally acceptable.`
    }
  }
  return {
    color: 'red',
    sign: 'exclamation',
    text: `Do you need to go back and make some changes? Great content relies on correctness and clarity, and an error rate below 1% is generally acceptable.`
  }
}
export const getReadabilityDetails = (
  readabilityCount: number
): AuditSetResponse => {
  if (readabilityCount >= 90) {
    return {
      color: 'green',
      sign: 'tick',
      text: `This content is very easy to read`
    }
  } else if (readabilityCount >= 80) {
    return {
      color: 'yellow',
      sign: 'exclamation',
      text: `This content is easy to read`
    }
  } else if (readabilityCount >= 70) {
    return {
      color: 'yellow',
      sign: 'exclamation',
      text: `This content is fairly easy to read`
    }
  } else if (readabilityCount >= 60) {
    return {
      color: 'yellow',
      sign: 'exclamation',
      text: `This content is in plain english`
    }
  } else if (readabilityCount >= 50) {
    return {
      color: 'yellow',
      sign: 'exclamation',
      text: `This content is fairly difficult to read`
    }
  } else if (readabilityCount >= 30) {
    return {
      color: 'orange',
      sign: 'exclamation',
      text: `This content is difficult to read`
    }
  } else if (readabilityCount >= 10) {
    return {
      color: 'orange',
      sign: 'exclamation',
      text: `This content is very difficult to read. Best understood by university graduates.`
    }
  }
  return {
    color: 'red',
    sign: 'exclamation',
    text: `This content is extremely difficult to read. Best understood by university graduates.`
  }
}
export const getWordCountDetails = (
  wordCountPercentage: number,
  words: number
): OptionalAuditSetResponse => {
  if (wordCountPercentage >= 20) {
    return {
      color: 'red',
      sign: 'exclamation',
      text: `The assignment requires an approximate word count of ${words}. Please add more content and submit the assignment again.`,
      header: `${wordCountPercentage}% below the required word count.`
    }
  } else if (wordCountPercentage > 10) {
    return {
      color: 'yellow',
      sign: 'exclamation',
      text: `The assignment requires an approximate word count of ${words}. Highly recommended to add more content here.`,
      header: `${wordCountPercentage}% below the required word count.`
    }
  }
  return null
}

export const getReadabilityScoreTooltipText = (
  readabilityScore: number | undefined
): string => {
  if (readabilityScore === undefined || readabilityScore === null) return ''
  if (readabilityScore >= 60) return 'Great job! Your content is easy to read.'
  if (readabilityScore < 60 && readabilityScore >= 30)
    return 'Your content is a bit difficult to read. Try using simpler words and sentences.'
  if (readabilityScore < 30)
    return 'Your content is extremely difficult to read. Try using simpler words and sentences.'
  return ''
}

export const getReadabilityScorePillClass = (
  readabilityScore: number | undefined | null
): string => {
  if (readabilityScore === undefined || readabilityScore === null) return 'Gray'
  if (readabilityScore >= 60) return 'Gray'
  if (readabilityScore < 60 && readabilityScore >= 30) return 'Yellow'
  if (readabilityScore < 30) return 'Red'
  return 'Gray'
}

export const getPlagiarismPillClassV2 = (plagiarismPercent: number): string => {
  if (plagiarismPercent >= 0 && plagiarismPercent <= 5) return 'Green'
  if (plagiarismPercent > 5) return 'Yellow'
  return 'Gray'
}

export const getAdherencePillClass = (adherence: number): string => {
  if (adherence === 0) return 'Green'
  else return 'Yellow'
}

export const isEligibleForAudit = (
  existingAuditData?: AuditData | null,
  verticalName?: string,
  language?: string,
  role?: CreatorRole
): boolean => {
  if (!existingAuditData?.data?.allowed_verticals) return false
  if (!['writer', 'editor'].includes(role ?? '')) return false
  if (language !== 'English') return false
  if (!verticalName) return false
  if (!existingAuditData.data.allowed_verticals.includes(verticalName))
    return false

  return true
}

export const getAuditPropertyKeys = (
  data: AuditDataSuccess | null | undefined,
  role: UserRole
): [
  (
    | 'writer_audits'
    | 'editor_submission_audits'
    | 'editor_audits'
    | 'writer_submission_audits'
  ),
  'editor_audit_limit' | 'writer_audit_limit'
] => {
  if (!data) return ['writer_audits', 'writer_audit_limit']
  if (role === 'editor') {
    if (data?.editor_audits?.length)
      return ['editor_audits', 'editor_audit_limit']
    if (data?.writer_submission_audits?.length)
      return ['writer_submission_audits', 'editor_audit_limit']
    if (data?.writer_audits?.length)
      return ['writer_audits', 'editor_audit_limit']
  }
  return ['writer_audits', 'writer_audit_limit']
}
